import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Image,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  FormHelperText,
  useToast,
  Icon,
  Spinner,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { FiUpload, FiCheck, FiX, FiImage, FiEdit3 } from 'react-icons/fi';
import { canvasAPI } from '../../services/api';
import SignatureCanvasComponent from './SignatureCanvas';

const SignatureUpload = () => {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentSignature, setCurrentSignature] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState(Date.now());
  const fileInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    loadSignature();
  }, []);

  useEffect(() => {
    // Cleanup preview URL
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const loadSignature = async () => {
    try {
      setLoading(true);
      const response = await canvasAPI.getMySignature();
      if (response.has_signature) {
        setCurrentSignature(response.signature);
      }
    } catch (error) {
      console.error('Error loading signature:', error);
      // No mostrar error si simplemente no hay firma
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Tipo de archivo inválido',
        description: 'Solo se permiten archivos PNG, JPG, JPEG o GIF',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Archivo muy grande',
        description: 'El archivo no debe superar 5MB',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSelectedFile(file);
    
    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      await canvasAPI.uploadSignature(selectedFile);
      
      toast({
        title: 'Firma subida exitosamente',
        description: 'Tu firma ha sido guardada correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Limpiar selección y recargar
      setSelectedFile(null);
      setPreviewUrl(null);
      setSignatureTimestamp(Date.now());
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      await loadSignature();
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast({
        title: 'Error al subir firma',
        description: error.response?.data?.detail || 'No se pudo subir la firma',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSignatureSave = async (file) => {
    setSelectedFile(file);
    // Crear preview del archivo del canvas
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Subir automáticamente
    try {
      setUploading(true);
      await canvasAPI.uploadSignature(file);
      
      toast({
        title: 'Firma dibujada subida exitosamente',
        description: 'Tu firma ha sido guardada correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Limpiar y recargar
      setSelectedFile(null);
      setPreviewUrl(null);
      setShowCanvas(false);
      setSignatureTimestamp(Date.now());
      await loadSignature();
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast({
        title: 'Error al subir firma',
        description: error.response?.data?.detail || 'No se pudo subir la firma',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCanvasCancel = () => {
    setShowCanvas(false);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando firma...</Text>
      </Box>
    );
  }

  return (
    <Box maxW="3xl" mx="auto" py={8} px={4}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Icon as={FiImage} boxSize={12} color="blue.500" mb={3} />
          <Heading size="lg" mb={2}>
            Gestionar Firma Digital
          </Heading>
          <Text color="gray.600">
            Sube tu firma para que aparezca en los certificados que emitas
          </Text>
        </Box>

        {/* Current Signature */}
        {currentSignature && !selectedFile && (
          <Card variant="outline" borderWidth="2px" borderColor="green.500">
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">Firma Actual</Heading>
                <Badge colorScheme="green" fontSize="sm">
                  Activa
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={6}
                  bg="gray.50"
                  textAlign="center"
                >
                  <Image
                    src={`${currentSignature.signature_path}?t=${signatureTimestamp}`}
                    alt="Firma actual"
                    maxH="150px"
                    mx="auto"
                    fallback={
                      <Text color="gray.500">
                        No se pudo cargar la imagen
                      </Text>
                    }
                  />
                </Box>
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Subida el{' '}
                  {new Date(currentSignature.uploaded_at).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Upload Form with Tabs */}
        <Card>
          <CardHeader>
            <Heading size="md">
              {currentSignature ? 'Actualizar Firma' : 'Subir Nueva Firma'}
            </Heading>
          </CardHeader>
          <CardBody>
            <Tabs colorScheme="blue" variant="enclosed">
              <TabList>
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiUpload} />
                    <Text>Subir Archivo</Text>
                  </HStack>
                </Tab>
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiEdit3} />
                    <Text>Dibujar Firma</Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Subir archivo */}
                <TabPanel>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Archivo de Imagen</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                  onChange={handleFileSelect}
                  display="none"
                />
                <Button
                  onClick={handleButtonClick}
                  leftIcon={<FiUpload />}
                  colorScheme="blue"
                  variant="outline"
                  width="full"
                  isDisabled={uploading}
                >
                  Seleccionar Archivo
                </Button>
                <FormHelperText>
                  Formatos permitidos: PNG, JPG, JPEG, GIF. Tamaño máximo: 5MB
                </FormHelperText>
              </FormControl>

              {/* Preview */}
              {previewUrl && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Vista Previa:
                  </Text>
                  <Box
                    borderWidth="2px"
                    borderColor="blue.500"
                    borderRadius="md"
                    p={6}
                    bg="gray.50"
                    textAlign="center"
                  >
                    <Image
                      src={previewUrl}
                      alt="Preview"
                      maxH="150px"
                      mx="auto"
                    />
                  </Box>
                  <Text fontSize="xs" color="gray.600" mt={2} textAlign="center">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </Text>
                </Box>
              )}

              {/* Actions */}
              {selectedFile && (
                <HStack spacing={3}>
                  <Button
                    flex={1}
                    colorScheme="green"
                    leftIcon={<FiCheck />}
                    onClick={handleUpload}
                    isLoading={uploading}
                    loadingText="Subiendo..."
                  >
                    Confirmar y Subir
                  </Button>
                  <Button
                    flex={1}
                    variant="outline"
                    leftIcon={<FiX />}
                    onClick={handleCancel}
                    isDisabled={uploading}
                  >
                    Cancelar
                  </Button>
                </HStack>
              )}
            </VStack>
                </TabPanel>

                {/* Tab 2: Dibujar firma */}
                <TabPanel>
                  <SignatureCanvasComponent
                    onSignatureSave={handleSignatureSave}
                    onCancel={handleCanvasCancel}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>

        {/* Info Alert */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Recomendaciones</AlertTitle>
            <AlertDescription fontSize="xs">
              <VStack align="start" spacing={1}>
                <Text>• Usa una imagen con fondo transparente (PNG) para mejores resultados</Text>
                <Text>• Asegúrate de que la firma sea clara y legible</Text>
                <Text>• La imagen será redimensionada automáticamente para los certificados</Text>
                {currentSignature && (
                  <Text>• Al subir una nueva firma, la anterior será reemplazada</Text>
                )}
              </VStack>
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>
    </Box>
  );
};

export default SignatureUpload;
